import { Injectable, inject } from '@angular/core';
import {
  DEFAULTS,
  ERROR_CODES,
  EXT_KEYS,
  EXT_PREFIX,
  FHIR_TYPES,
  OTHER_INPUT_SUBCONTROL_EXT,
  QUESTIONNAIRE_STATUS,
  SUBCONTROL_DEPENDENCY_ACTION,
} from '../constants/fhir.constants';
import {
  ControlMetadataInfo,
  ConversionError,
  ConversionMetrics,
  ConversionOptions,
  ConversionResult,
  DesignerQuestionnaireOptions,
  IControlCard,
  IDesignerFhirConverter,
} from '../interfaces/fhir-converter.interfaces';
import { CONTROL_METADATA_PROVIDER, IControlComponentMetadata } from '../interfaces/metadata-provider.interface';
import { FHIRAnswerOption, FHIRItem, FHIRItemType } from '../models/fhir.model';
import { ChoiceOptionLike, findMatchingChoiceOption, isSpecialOptionMatch } from '../utilities/choice-option-matcher';
import { applyLegacyOtherInputSettings } from '../../utils/helpers/other-input-migration';
import { FhirExtensionBuilder } from './fhir-extension.builder';
import { FhirValidator } from './fhir.validator';
import { LinkIdGenerator } from './link-id.generator';
import { QuestionnaireFormatVersionService } from './questionnaire-format-version.service';

/**
 * Designer to FHIR Converter Service
 *
 * Converts designer control cards (IControlCard) to FHIR Questionnaire items. Uses IControlMetadataProvider for control
 * metadata lookup.
 *
 * Note: Not providedIn: 'root' because it needs to be provided in the app context where CONTROL_METADATA_PROVIDER is
 * configured.
 */
@Injectable()
export class DesignerFhirConverter implements IDesignerFhirConverter {
  private readonly metadataProvider = inject(CONTROL_METADATA_PROVIDER, { optional: true });
  private readonly linkIdGenerator = inject(LinkIdGenerator);
  private readonly extensionBuilder = inject(FhirExtensionBuilder);
  private readonly validator = inject(FhirValidator);
  private readonly formatVersionService = inject(QuestionnaireFormatVersionService);

  /** Metadata cache for performance */
  private readonly metadataCache = new Map<string, IControlComponentMetadata | null>();

  /** Convert a single designer control to a FHIR item */
  convertControl(control: IControlCard, index: number, parentPath: string, bodyImageRegions?: any[]): FHIRItem | null {
    // Handle tab groups with special case - they don't have registry metadata
    if ((control as any).__tabGroup === true) {
      return this.convertTabGroup(control, parentPath, bodyImageRegions);
    }

    // Card Link Group is a custom grouped control with card-level child items.
    if ((control as any).controlType === 'card-link-group' || (control as any).controlType === 'card-link-group-v1') {
      return this.convertCardLinkGroup(control, parentPath, bodyImageRegions);
    }

    // Determine control type for regular controls
    const controlType = (control as any).controlType || (control as any).type;

    // Get metadata for the control
    const metadata = this.getCachedMetadata(controlType);

    if (!metadata) {
      return null;
    }

    // Preserve existing linkId if present, otherwise generate
    const linkId =
      (control as any).linkId ||
      this.linkIdGenerator.generateControlLinkId(
        controlType,
        parentPath,
        metadata.isLayoutControl || false,
        control.question || control.label
      );

    // For subsections (and all controls), prefer componentProps.label as the source of truth
    // since it is always kept up-to-date by the properties panel and inline label editor.
    // Fall back to control.label (root property) and then control.question for backwards compatibility.
    const controlLabel = (control.componentProps as any)?.label || control.label || (control as any).question || '';

    const item: FHIRItem = {
      linkId,
      text: controlLabel,
      type: (metadata.fhirType as FHIRItemType) || FHIR_TYPES.STRING,
    };

    // Handle layout controls with children
    if (metadata.isLayoutControl && control.children) {
      const childrenToConvert = this.resolveLayoutChildrenForExport(controlType, control);
      item.item = this.convertControls(childrenToConvert, linkId);
    }

    // Handle choice-based controls - prefer componentProps as source-of-truth
    // Skip options when dataMode is 'api' - options are only for static mode
    const dataMode = (control.componentProps as any)?.dataMode;
    const options = (control.componentProps as any)?.options || control.options;

    if (metadata.supportsOptions && options && Array.isArray(options) && options.length > 0 && dataMode !== 'api') {
      item.answerOption = this.buildAnswerOptions(control);
    }

    const controlWithRequiredState = this.withAggregatedRequired(control);

    // Ensure isVisible has a default value (true if not explicitly set)
    const controlWithDefaults = this.withDefaultVisibility(controlWithRequiredState);

    // Add extensions
    const extensions = this.extensionBuilder.buildControlExtensions(controlWithDefaults, this.toMetadataInfo(metadata));

    // Add custom logic extension if fhirPath exists (stringified JSON)
    const fhirPathExpression = (control as { fhirPath?: string }).fhirPath;
    if (fhirPathExpression) {
      extensions.push({
        url: `${this.extensionBuilder.getExtPrefix()}:${EXT_KEYS.DEPENDENCIES}`,
        valueString: fhirPathExpression,
      });
    }
    // Add body image regions to ext:ui extension for subsections
    if (controlType === 'subsection' && bodyImageRegions && bodyImageRegions.length > 0) {
      const bodyImageData = bodyImageRegions.filter((region: any) => region.subsectionId === control.linkId);

      if (bodyImageData.length > 0) {
        // Find existing ext:ui extension
        const uiExtensionIndex = extensions.findIndex((ext: any) => ext.url && ext.url.includes('ext:ui'));

        if (uiExtensionIndex !== -1) {
          // Add body image data to existing ext:ui extension
          const existingExt = extensions[uiExtensionIndex];
          if (existingExt) {
            const uiValue =
              typeof existingExt.valueString === 'string'
                ? JSON.parse(existingExt.valueString)
                : existingExt.valueString || {};

            extensions[uiExtensionIndex] = {
              ...existingExt,
              url: existingExt.url,
              valueString: JSON.stringify({
                ...uiValue,
                bodyImageRegions: bodyImageData.map((region: any) => ({
                  subsectionId: region.subsectionId,
                  subsectionName: region.subsectionName,
                  coordinates: {
                    x: region.coordinates.x,
                    y: region.coordinates.y,
                  },
                  viewportCoordinates: {
                    x: region.viewportCoordinates.x,
                    y: region.viewportCoordinates.y,
                  },
                })),
              }),
            };
          }
        } else {
          // Create ext:ui extension with body image data
          extensions.push({
            url: `${this.extensionBuilder.getExtPrefix()}:ui`,
            valueString: JSON.stringify({
              bodyImageRegions: bodyImageData.map((region: any) => ({
                subsectionId: region.subsectionId,
                subsectionName: region.subsectionName,
                coordinates: {
                  x: region.coordinates.x,
                  y: region.coordinates.y,
                },
                viewportCoordinates: {
                  x: region.viewportCoordinates.x,
                  y: region.viewportCoordinates.y,
                },
              })),
            }),
          });
        }
      }
    }

    if (extensions.length > 0) {
      item.extension = extensions;
    }

    if (controlType === 'subsection') {
      // Add showStatus and amend to the UI config extension for subsection controls
      if (!item.extension) {
        item.extension = [];
      }

      // Extract amend property (default to true if not specified)
      const amend = (control.componentProps as any)?.amend ?? (control as any).amend ?? true;

      // Find or create the UI config extension
      let uiExt = item.extension.find((ext) => ext.url === this.extensionBuilder.getUiConfigExt());

      if (uiExt && uiExt.valueString) {
        // Parse existing UI config and add showStatus and amend
        try {
          const uiConfig = JSON.parse(uiExt.valueString);
          uiConfig.showStatus = true;
          if (amend !== undefined) {
            uiConfig.amend = amend;
          }
          uiExt.valueString = JSON.stringify(uiConfig);
        } catch {
          // If parsing fails, create new config
          uiExt.valueString = JSON.stringify({ showStatus: true, amend });
        }
      } else {
        // Create new UI config extension with showStatus and amend
        item.extension.push({
          url: this.extensionBuilder.getUiConfigExt(),
          valueString: JSON.stringify({ showStatus: true, amend }),
        });
      }
    }
    // Handle radio-group initial value (selected option) and sanitize UI extension value
    if (controlType === 'radio-group' || controlType === 'radio') {
      // Use !== undefined so that an explicit null (user cleared to "None") is respected and not
      // overridden by the stale root-level control.value that was set on template load.
      const componentProps = control.componentProps as Record<string, unknown> | undefined;
      const componentPropsValue = componentProps?.['value'];
      const rawControlValue = (control as Record<string, unknown>)['value'];
      const selectedValue = (componentPropsValue !== undefined ? componentPropsValue : rawControlValue) as
        string | undefined;
      const options = (componentProps?.['options'] || control.options || []) as ChoiceOptionLike[];

      const matchedOption = findMatchingChoiceOption(selectedValue, options);
      const isSpecial = isSpecialOptionMatch(selectedValue, control, options);
      const isValidOption = isSpecial || !!matchedOption;

      if (selectedValue && isValidOption) {
        const display = matchedOption?.label || matchedOption?.display || selectedValue;
        const code = matchedOption?.code
          ? String(matchedOption.code)
          : matchedOption?.value !== undefined && matchedOption?.value !== null
            ? String(matchedOption.value)
            : selectedValue;
        item.initial = [
          {
            valueCoding: {
              code,
              display,
            },
          },
        ];
      } else {
        // Clean up in-memory control card so stale value does not linger
        if (componentProps && 'value' in componentProps) {
          delete componentProps['value'];
        }
        if ('value' in (control as Record<string, unknown>)) {
          delete (control as Record<string, unknown>)['value'];
        }

        // Ensure UI config extension does not retain unmatched value
        if (item.extension) {
          const uiExt = item.extension.find((ext) => ext.url === this.extensionBuilder.getUiConfigExt());
          if (uiExt && typeof uiExt.valueString === 'string') {
            try {
              const parsedUi = JSON.parse(uiExt.valueString) as Record<string, unknown>;
              if ('value' in parsedUi) {
                delete parsedUi['value'];
                uiExt.valueString = JSON.stringify(parsedUi);
              }
            } catch {
              // Ignore parse error
            }
          }
        }
      }
    }

    // Nest option-based sub-controls (children with __parentOptionId) inside the parent item.
    // Each child is converted and tagged with __parentOptionId in its extension so the
    // renderer can position it beneath the correct option.
    // NOTE: Sub-controls are now flattened to siblings by convertControls().
    // This block is kept only for layout controls that legitimately nest children.
    // Option-based sub-controls are handled in convertControls() via flattenOptionSubControls().

    // Handle rating control initial value
    if (controlType === 'rating') {
      const selectedValue = (control.componentProps as any)?.value || (control as any).value;
      if (selectedValue != null && selectedValue !== '') {
        const numValue = typeof selectedValue === 'string' ? parseInt(selectedValue, 10) : Number(selectedValue);
        if (!isNaN(numValue)) {
          // Get scaleCount and clamp value to valid range (1 to scaleCount)
          const scaleCount = (control.componentProps as any)?.scaleCount || (control as any).scaleCount || 5;
          const maxScale = typeof scaleCount === 'string' ? parseInt(scaleCount, 10) : Number(scaleCount);
          const clampedValue = Math.min(Math.max(numValue, 1), maxScale);

          item.initial = [
            {
              valueInteger: clampedValue,
            },
          ];
        }
      }
    }

    return item;
  }

  /**
   * Resolve layout children before export. For tabbed-layout, tab-group labels should mirror the current tabs config so
   * exported FHIR text stays in sync.
   */
  private resolveLayoutChildrenForExport(controlType: string, control: IControlCard): IControlCard[] {
    const children = control.children ?? [];

    if (controlType !== 'tabbed-layout' || children.length === 0) {
      return children;
    }

    const tabs = (control.componentProps as any)?.tabs;
    if (!Array.isArray(tabs) || tabs.length === 0) {
      return children;
    }

    const tabMetadataByKey = new Map<string, { label: string; questionTitle?: string }>();
    for (const tab of tabs) {
      const key = typeof tab?.key === 'string' ? tab.key : undefined;
      const label = typeof tab?.label === 'string' ? tab.label.trim() : undefined;
      if (key && label) {
        const tabMetadata: { label: string; questionTitle?: string } = { label };
        if (typeof tab?.questionTitle === 'string') {
          tabMetadata.questionTitle = tab.questionTitle;
        }
        tabMetadataByKey.set(key, tabMetadata);
      }
    }

    if (tabMetadataByKey.size === 0) {
      return children;
    }

    return children.map((child) => {
      const isTabGroup = (child as any).__tabGroup === true;
      const tabKey = (child as any).tabKey;

      if (!isTabGroup || typeof tabKey !== 'string') {
        return child;
      }

      const resolvedTab = tabMetadataByKey.get(tabKey);
      if (!resolvedTab) {
        return child;
      }

      const currentQuestionTitle = (child as any).componentProps?.questionTitle;
      const hasQuestionTitle = Object.prototype.hasOwnProperty.call(resolvedTab, 'questionTitle');
      const questionTitleChanged = hasQuestionTitle && currentQuestionTitle !== resolvedTab.questionTitle;
      const labelChanged = child.label !== resolvedTab.label;

      if (!labelChanged && !questionTitleChanged) {
        return child;
      }

      return {
        ...child,
        label: resolvedTab.label,
        question: resolvedTab.label,
        componentProps: {
          ...((child as any).componentProps || {}),
          ...(hasQuestionTitle ? { questionTitle: resolvedTab.questionTitle } : {}),
        },
      };
    });
  }

  /** Convert tab group control - special handling since they don't have registry metadata */
  private convertTabGroup(control: IControlCard, parentPath: string, bodyImageRegions?: any[]): FHIRItem {
    const tabKey = (control as any).tabKey || 'tab';
    const linkId =
      (control as any).linkId || this.linkIdGenerator.generateControlLinkId('__tab-group', parentPath, true, tabKey);

    const item: FHIRItem = {
      linkId,
      text: control.label || tabKey,
      type: FHIR_TYPES.GROUP as FHIRItemType,
    };

    // Process children - tab groups are layout controls
    if (control.children?.length) {
      item.item = this.convertControls(control.children, linkId, bodyImageRegions);
    }

    // Build extensions with tab-specific metadata
    const uiConfig = {
      componentType: 'HxTabContent',
      package: '@complexcare/harmony',
      category: 'layout',
      isLayoutControl: true,
      __isTabContent: true,
      tabKey,
      questionTitle:
        typeof (control.componentProps as any)?.questionTitle === 'string'
          ? (control.componentProps as any).questionTitle
          : undefined,
    };

    item.extension = [
      {
        url: this.extensionBuilder.getUiConfigExt(),
        valueString: JSON.stringify(uiConfig),
      },
    ];

    // Add custom logic extension if fhirPath exists (dependencies for tab visibility)
    const fhirPathExpression = (control as { fhirPath?: string }).fhirPath;
    if (fhirPathExpression) {
      item.extension.push({
        url: `${this.extensionBuilder.getExtPrefix()}:${EXT_KEYS.DEPENDENCIES}`,
        valueString: fhirPathExpression,
      });
    }

    return item;
  }

  /** Convert card-link-group control into a grouped Questionnaire item with one child per card. */
  private convertCardLinkGroup(control: IControlCard, parentPath: string, bodyImageRegions?: any[]): FHIRItem {
    const linkId =
      (control as any).linkId ||
      this.linkIdGenerator.generateControlLinkId(
        'card-link-group',
        parentPath,
        true,
        control.question || control.label
      );

    const controlLabel = (control.componentProps as any)?.label || control.label || (control as any).question || '';

    const item: FHIRItem = {
      linkId,
      text: controlLabel,
      type: FHIR_TYPES.GROUP as FHIRItemType,
    };

    const cards = this.resolveCardLinkCards(control);
    if (cards.length > 0) {
      item.item = cards.map((card, index) => this.convertCardLinkChildItem(card, linkId, index));
    }

    const controlAsAny = control as any;
    const {
      cards: _omitRootCards,
      selectedCardIndex: _omitRootSelectedCardIndex,
      ...controlWithoutDesignerOnlyRootFields
    } = controlAsAny;

    const nextComponentProps = {
      ...((control.componentProps as Record<string, unknown> | undefined) ?? {}),
    };
    delete (nextComponentProps as any)['cards'];
    delete (nextComponentProps as any)['selectedCardIndex'];

    const controlForExtension = {
      ...controlWithoutDesignerOnlyRootFields,
      componentProps: nextComponentProps,
    } as IControlCard;

    const controlExtensions = this.extensionBuilder.buildControlExtensions(controlForExtension, {
      label: controlLabel,
      componentType: 'HxCardLinkGroup',
      package: '@complexcare/harmony',
      category: 'custom',
      isLayoutControl: false,
      fhirType: 'group',
      supportsOptions: false,
      propertyDefinitions: [],
    });

    if (controlExtensions.length > 0) {
      item.extension = controlExtensions;
    }

    const fhirPathExpression = (control as { fhirPath?: string }).fhirPath;
    if (fhirPathExpression) {
      if (!item.extension) item.extension = [];
      item.extension.push({
        url: `${this.extensionBuilder.getExtPrefix()}:${EXT_KEYS.DEPENDENCIES}`,
        valueString: fhirPathExpression,
      });
    }

    return item;
  }

  private resolveCardLinkCards(control: IControlCard): Array<Record<string, unknown>> {
    const componentCards = Array.isArray((control.componentProps as any)?.cards)
      ? ((control.componentProps as any).cards as Array<Record<string, unknown>>)
      : [];
    if (componentCards.length > 0) {
      return componentCards;
    }

    return Array.isArray((control as any).cards) ? ((control as any).cards as Array<Record<string, unknown>>) : [];
  }

  private convertCardLinkChildItem(card: Record<string, unknown>, parentLinkId: string, index: number): FHIRItem {
    const title = String(card['title'] ?? card['label'] ?? '').trim();
    const linkUrl = String(card['linkUrl'] ?? '').trim();
    const questionTitle = String(card['questionTitle'] ?? '').trim();
    const cardId = String(card['id'] ?? card['value'] ?? index + 1);
    const safeCardKey = cardId.replace(/[^a-zA-Z0-9_-]/g, '_');

    const cardUiConfig = {
      componentType: 'HxCardLinkGroupCard',
      package: '@complexcare/harmony',
      category: 'custom',
      isLayoutControl: false,
      __isCardLinkCard: true,
      cardId,
      title,
      subtitle: String(card['subtitle'] ?? ''),
      linkLabel: String(card['linkLabel'] ?? ''),
      linkUrl,
      linkTarget: String(card['openLinkIn'] ?? ''),
      defaultToggleState: card['defaultToggleState'],
      notesRequiredWhenToggleOn: card['notesRequiredWhenToggleOn'],
      questionTitle: questionTitle || undefined,
    };

    const childItem: FHIRItem = {
      linkId: `${parentLinkId}.__card-${safeCardKey}`,
      text: questionTitle || title,
      type: FHIR_TYPES.GROUP as FHIRItemType,
      extension: [
        {
          url: this.extensionBuilder.getUiConfigExt(),
          valueString: JSON.stringify(cardUiConfig),
        },
      ],
    };

    const fhirPathExpression = typeof card['fhirPath'] === 'string' ? String(card['fhirPath']).trim() : '';
    if (fhirPathExpression) {
      childItem.extension!.push({
        url: `${this.extensionBuilder.getExtPrefix()}:${EXT_KEYS.DEPENDENCIES}`,
        valueString: fhirPathExpression,
      });
    }

    return childItem;
  }

  /** Convert array of controls to FHIR items */
  convertControls(controls: IControlCard[], parentPath: string, bodyImageRegions?: any[]): FHIRItem[] {
    const items: FHIRItem[] = [];

    // Filter out undefined controls and process each one
    const validControls = controls.filter((control): control is IControlCard => control !== undefined);

    for (let i = 0; i < validControls.length; i++) {
      const control = validControls[i];
      if (!control) {
        continue;
      }
      const item = this.convertControl(control, i, parentPath, bodyImageRegions);
      if (item) {
        items.push(item);

        // Flatten option-based sub-controls as siblings with dependency + subcontrol-parent extensions
        if (this.hasOptionSubControls(control)) {
          const subItems = this.flattenOptionSubControls(control, item.linkId);
          items.push(...subItems);
        }
      }
    }

    return items;
  }

  /**
   * Check if a control has option-based sub-controls (children tagged with __parentOptionId). Returns true for any
   * choice control (radio-group, checkbox-group, etc.) that manages sub-controls beneath its options. Safe for all
   * other controls — they never have __parentOptionId-tagged children.
   */
  private hasOptionSubControls(control: IControlCard): boolean {
    if (this.hasEnabledOtherOption(control) && !this.findExistingOtherInputChild(control)) {
      return true;
    }
    if (!control.children || control.children.length === 0) {
      return false;
    }
    return control.children.some(
      (child) => !!(child as any).__parentOptionId || !!(child.componentProps as any)?.__parentOptionId
    );
  }

  /** True when the control has its "Other" option enabled (hasOther / showOtherOption, root or componentProps). */
  private hasEnabledOtherOption(control: IControlCard): boolean {
    const componentProps = (control.componentProps as Record<string, unknown> | undefined) ?? {};
    const rootProps = control as Record<string, unknown>;
    const options: any[] = (componentProps['options'] as any[]) || (rootProps['options'] as any[]) || [];
    const hasOtherInOptions = options.some(
      (o) =>
        o &&
        (String(o.id).toLowerCase() === 'other' ||
          String(o.value).toLowerCase() === 'other' ||
          String(o.label).toLowerCase() === 'other')
    );
    return (
      componentProps['hasOther'] === true ||
      componentProps['showOtherOption'] === true ||
      componentProps['showOtherInput'] === true ||
      rootProps['hasOther'] === true ||
      rootProps['showOtherOption'] === true ||
      rootProps['showOtherInput'] === true ||
      hasOtherInOptions
    );
  }

  /** Finds an already-synthesized "Other" text-input child among a control's children, if any. */
  private findExistingOtherInputChild(control: IControlCard): IControlCard | undefined {
    return (control.children ?? []).find(
      (child) => !!(child as any)?.__isOtherInput || !!(child?.componentProps as any)?.__isOtherInput
    );
  }

  /** Resolves the option value that represents "Other" on a control's options list, defaulting to the literal 'other'. */
  private resolveOtherOptionValue(control: IControlCard): string {
    const customOther = (control.componentProps as any)?.otherValue ?? (control as any)?.otherValue;
    if (customOther !== undefined && customOther !== null) {
      return String(customOther);
    }
    const options: any[] = (control.componentProps as any)?.options || (control as any)?.options || [];
    const matched = options.find(
      (o) =>
        o &&
        (String(o.id).toLowerCase() === 'other' ||
          String(o.value).toLowerCase() === 'other' ||
          String(o.label).toLowerCase() === 'other')
    );
    return matched?.value !== undefined && matched?.value !== null ? String(matched.value) : 'other';
  }

  /**
   * Synthesizes the auto-generated "Other" text-input sub-control for a radio/checkbox control with "Other" enabled.
   * Reuses the exact same shape as a manually-added subcontrol (tagged with __parentOptionId) so it flows through the
   * existing flattening/dependency-rule pipeline unchanged; additionally tagged with __isOtherInput so parsers can
   * distinguish it from a user-authored subcontrol.
   */
  private synthesizeOtherInputControl(parentControl: IControlCard, parentLinkId: string): IControlCard {
    const optionValue = this.resolveOtherOptionValue(parentControl);
    const linkId = `${parentLinkId}.other`;
    const parentProps = (parentControl.componentProps as Record<string, unknown> | undefined) ?? {};
    const childProps: Record<string, unknown> = {
      __parentOptionId: optionValue,
      __isOtherInput: true,
    };
    applyLegacyOtherInputSettings(childProps, parentProps, (parentControl as any).question);
    return {
      id: linkId,
      controlType: DEFAULTS.CONTROL_TYPE,
      linkId,
      icon: '',
      question: '',
      isRequired: false,
      hasOptions: false,
      __parentOptionId: optionValue,
      __isOtherInput: true,
      componentProps: childProps,
    } as IControlCard;
  }

  /**
   * Flatten option-based sub-controls as sibling FHIR items. Each sub-control gets:
   *
   * Each sub-control gets a `urn:ccs:ext:dependencies` entry with `Action: 'subcontrol'`. The `field` is parentLinkId,
   * `value` is optionValue — this is both the structural marker and the visibility rule. The runtime treats
   * 'subcontrol' the same as 'visible'.
   */
  private flattenOptionSubControls(parentControl: IControlCard, parentLinkId: string): FHIRItem[] {
    const children = [...(parentControl.children ?? [])];
    const subItems: FHIRItem[] = [];

    // Auto-inject the "Other" text-input as a virtual child, unless one was already synthesized/authored.
    if (this.hasEnabledOtherOption(parentControl) && !this.findExistingOtherInputChild(parentControl)) {
      children.push(this.synthesizeOtherInputControl(parentControl, parentLinkId));
    }

    for (let idx = 0; idx < children.length; idx++) {
      const child = children[idx];
      if (!child) {
        continue;
      }

      const parentOptionId = (child as any).__parentOptionId || (child.componentProps as any)?.__parentOptionId;
      if (!parentOptionId) {
        continue;
      }

      const parentOptions: any[] =
        (parentControl.componentProps as any)?.options || (parentControl as any)?.options || [];
      const matchedOption = parentOptions.find(
        (o) =>
          o &&
          (String(o.id) === String(parentOptionId) ||
            String(o.value) === String(parentOptionId) ||
            String(o.label) === String(parentOptionId))
      );
      const optionValue = matchedOption?.value ?? parentOptionId;

      const childItem = this.convertControl(child, idx, parentLinkId);
      if (!childItem) {
        continue;
      }

      if (!childItem.extension) {
        childItem.extension = [];
      }

      const isOtherInput = !!(child as any).__isOtherInput || !!(child.componentProps as any)?.__isOtherInput;
      if (isOtherInput) {
        childItem.extension.push({ url: OTHER_INPUT_SUBCONTROL_EXT, valueBoolean: true });
      }

      // Auto-generate dependency rule: Action 'subcontrol' is the structural+visibility marker.
      // field = parentLinkId, value = optionValue — no separate subcontrol-parent extension needed.
      const autoRule = this.buildSubControlDependencyRule(parentLinkId, optionValue);

      // Merge with any existing dependency rules on the child
      const existingDeps = childItem.extension.find(
        (e) => e.url === `${this.extensionBuilder.getExtPrefix()}:${EXT_KEYS.DEPENDENCIES}`
      );

      let existingRules: any[] = [];
      if (existingDeps?.valueString) {
        try {
          const existing = JSON.parse(existingDeps.valueString);
          if (Array.isArray(existing)) {
            existingRules = existing;
          } else if (existing && Array.isArray(existing.conditionalLogic)) {
            existingRules = existing.conditionalLogic;
          } else if (existing) {
            existingRules = [existing];
          }
        } catch {
          // If parse fails, ignore existing
        }
      }

      const mergedRules = [autoRule, ...existingRules];
      const newValueString = JSON.stringify({
        conditionalLogic: mergedRules,
        dependencyCount: mergedRules.length,
      });

      if (existingDeps) {
        existingDeps.valueString = newValueString;
      } else {
        childItem.extension.push({
          url: `${this.extensionBuilder.getExtPrefix()}:${EXT_KEYS.DEPENDENCIES}`,
          valueString: newValueString,
        });
      }

      subItems.push(childItem);

      // Recursive check for grandchild sub-controls!
      if (this.hasOptionSubControls(child)) {
        const grandSubItems = this.flattenOptionSubControls(child, childItem.linkId);
        subItems.push(...grandSubItems);
      }
    }

    return subItems;
  }

  /**
   * Build a subcontrol dependency rule. Action 'subcontrol' is the unambiguous structural+visibility marker — distinct
   * from user-authored 'visible' rules so it can be cleanly stripped on import. field = parentLinkId, value =
   * optionValue. Runtime treats 'subcontrol' the same as 'visible'.
   */
  private buildSubControlDependencyRule(parentLinkId: string, optionValue: string): { Action: string; Rules: any[] } {
    if (optionValue === '*') {
      return {
        Action: SUBCONTROL_DEPENDENCY_ACTION,
        Rules: [
          {
            operator: null,
            field: parentLinkId,
            condition: 'NotEqualTo',
            value: '',
          },
        ],
      };
    }

    return {
      Action: SUBCONTROL_DEPENDENCY_ACTION,
      Rules: [
        {
          operator: null,
          field: parentLinkId,
          condition: 'Contains',
          value: optionValue,
        },
      ],
    };
  }

  /** Generate complete FHIR Questionnaire from designer controls */
  generateQuestionnaire(options: DesignerQuestionnaireOptions): string {
    const bodyImageRegions = (options as any).bodyImageRegions;
    let items: FHIRItem[];

    // If sections are provided, create multiple section items
    if (options.sections && options.sections.length > 0) {
      items = options.sections.map((section) => {
        // Use the persisted linkId from the store (generated at section creation time)
        const sectionLinkId = (section as any).linkId || this.linkIdGenerator.generateFromTitle(section.title);
        const controlItems = this.convertControls(section.controls, sectionLinkId, bodyImageRegions);
        const required = this.hasRequiredDescendant(section.controls);
        // Extract properties with defaults; aiExtraction/amend default to true when omitted
        const isVisible = section.isVisible !== undefined ? section.isVisible : true;
        const pdfExport = (section as any).pdfExport !== undefined ? (section as any).pdfExport : true;
        const aiExtraction =
          (section as any).aiExtraction !== undefined
            ? (section as any).aiExtraction
            : (section as any).isAiExtractionEnabled !== undefined
              ? (section as any).isAiExtractionEnabled
              : true;
        const amend = (section as any).amend !== undefined ? (section as any).amend : true;
        const extensions = this.extensionBuilder.buildSectionExtensions(
          section.icon,
          (section as any).assessmentStrategy,
          (section as any).assessmentPosition,
          (section as any).id,
          required,
          (section as any).questionTitle,
          isVisible,
          pdfExport,
          aiExtraction,
          amend,
          (section as any).tag,
          (section as any).sectionRequired === true
        );

        return {
          linkId: sectionLinkId,
          text: section.title,
          type: FHIR_TYPES.GROUP,
          extension: extensions.length > 0 ? extensions : undefined,
          item: controlItems.length > 0 ? controlItems : undefined,
        } as FHIRItem;
      });
    } else {
      // Fallback to single section with current controls
      // Use the persisted linkId from the store
      const sectionLinkId =
        (options as any).linkId || this.linkIdGenerator.generateFromTitle(options.title || DEFAULTS.UNTITLED_SECTION);
      const controlItems = this.convertControls(options.controls, sectionLinkId, bodyImageRegions);
      const required = this.hasRequiredDescendant(options.controls);
      // Extract properties with defaults; aiExtraction/amend default to true when omitted
      const isVisible = (options as any).isVisible !== undefined ? (options as any).isVisible : true;
      const pdfExport = (options as any).pdfExport !== undefined ? (options as any).pdfExport : true;
      const aiExtraction =
        (options as any).aiExtraction !== undefined
          ? (options as any).aiExtraction
          : (options as any).isAiExtractionEnabled !== undefined
            ? (options as any).isAiExtractionEnabled
            : true;
      const amend = (options as any).amend !== undefined ? (options as any).amend : true;
      const extensions = this.extensionBuilder.buildSectionExtensions(
        options.icon,
        (options as any).assessmentStrategy,
        (options as any).assessmentPosition,
        (options as any).sectionId,
        required,
        (options as any).questionTitle,
        isVisible,
        pdfExport,
        aiExtraction,
        amend,
        (options as any).tag,
        (options as any).sectionRequired === true
      );

      items = [
        {
          linkId: sectionLinkId,
          text: options.title || DEFAULTS.UNTITLED_SECTION,
          type: FHIR_TYPES.GROUP,
          extension: extensions.length > 0 ? extensions : undefined,
          item: controlItems.length > 0 ? controlItems : undefined,
        } as FHIRItem,
      ];
    }

    const questionnaire = {
      resourceType: 'Questionnaire',
      id: options.id || this.linkIdGenerator.generateUUID(),
      status: options.status || QUESTIONNAIRE_STATUS.DRAFT,
      item: items,
    };
    this.formatVersionService.stampOtherInputFormat(questionnaire);

    // Post-process: add ext:hierarchy extensions to controls
    this.addHierarchyExtensions(items);

    return JSON.stringify(questionnaire, null, 2);
  }

  /** Convert with detailed result including errors, warnings, and metrics */
  convertWithResult(
    options: DesignerQuestionnaireOptions,
    conversionOptions: ConversionOptions = {}
  ): ConversionResult<string> {
    const startTime = performance.now();
    const errors: ConversionError[] = [];
    const warnings: string[] = [];
    const metrics = this.createEmptyMetrics();
    const bodyImageRegions = (options as any).bodyImageRegions;

    try {
      if (!options.controls) {
        errors.push({
          code: ERROR_CODES.INVALID_INPUT,
          message: 'Controls array is required',
          severity: 'error',
        });
        return this.createResult(false, '', errors, warnings, metrics, startTime);
      }

      this.countItems(options.controls, metrics);

      // Use the persisted linkId from the store
      const sectionLinkId =
        (options as any).linkId || this.linkIdGenerator.generateFromTitle(options.title || DEFAULTS.UNTITLED_SECTION);

      const controlItems = this.convertControlsWithErrors(
        options.controls,
        sectionLinkId,
        errors,
        warnings,
        bodyImageRegions
      );

      const extensions = this.extensionBuilder.buildSectionExtensions(
        options.icon,
        (options as any).assessmentStrategy,
        (options as any).assessmentPosition,
        (options as any).sectionId,
        this.hasRequiredDescendant(options.controls),
        (options as any).questionTitle,
        (options as any).isVisible ?? true,
        (options as any).pdfExport ?? true,
        (options as any).aiExtraction ?? (options as any).isAiExtractionEnabled ?? true,
        (options as any).amend ?? true,
        (options as any).tag,
        (options as any).sectionRequired === true
      );

      metrics.extensionsCount = extensions.length;

      const questionnaire = {
        resourceType: 'Questionnaire',
        id: options.id || this.linkIdGenerator.generateUUID(),
        status: options.status || QUESTIONNAIRE_STATUS.DRAFT,
        item: [
          {
            linkId: sectionLinkId,
            text: options.title || DEFAULTS.UNTITLED_SECTION,
            type: FHIR_TYPES.GROUP as FHIRItemType,
            extension: extensions.length > 0 ? extensions : undefined,
            item: controlItems.length > 0 ? controlItems : undefined,
          },
        ],
      };
      this.formatVersionService.stampOtherInputFormat(questionnaire);

      const finalQuestionnaire = conversionOptions.itemTransformer
        ? { ...questionnaire, item: questionnaire.item.map(conversionOptions.itemTransformer) }
        : questionnaire;

      if (conversionOptions.validate) {
        this.validator.validate(finalQuestionnaire, errors, warnings);
      }

      const indent = conversionOptions.prettyPrint !== false ? 2 : 0;
      const json = JSON.stringify(finalQuestionnaire, null, indent);
      metrics.jsonSizeBytes = new Blob([json]).size;

      const hasErrors = errors.some((e) => e.severity === 'error');
      return this.createResult(!hasErrors, json, errors, warnings, metrics, startTime);
    } catch (error) {
      errors.push({
        code: ERROR_CODES.CONVERSION_ERROR,
        message: error instanceof Error ? error.message : 'Unknown conversion error',
        severity: 'error',
      });
      return this.createResult(false, '', errors, warnings, metrics, startTime);
    }
  }

  /** Clear metadata cache */
  clearCache(): void {
    this.metadataCache.clear();
  }

  // ============================================================================
  // PRIVATE METHODS
  // ============================================================================

  private getCachedMetadata(controlType: string): IControlComponentMetadata | null {
    if (!this.metadataCache.has(controlType)) {
      const metadata = this.metadataProvider?.getMetadata(controlType) ?? null;
      this.metadataCache.set(controlType, metadata);
    }
    return this.metadataCache.get(controlType) ?? null;
  }

  private toMetadataInfo(metadata: IControlComponentMetadata): ControlMetadataInfo {
    return {
      componentType: metadata.componentType,
      package: metadata.package,
      category: metadata.category,
      isLayoutControl: metadata.isLayoutControl,
      fhirType: metadata.fhirType,
      label: metadata.label,
      supportsOptions: metadata.supportsOptions,
      propertyDefinitions: metadata.propertyDefinitions,
    };
  }

  private withAggregatedRequired(control: IControlCard): IControlCard {
    if ((control as any).controlType !== 'subsection') {
      return control;
    }

    const required = this.hasRequiredDescendant(control.children);
    return {
      ...control,
      isRequired: required,
      componentProps: {
        ...(control.componentProps ?? {}),
        required,
      },
    };
  }

  /**
   * Ensure control has a default isVisible value (true if not explicitly set). This ensures isVisible is always
   * captured in FHIR extensions.
   */
  private withDefaultVisibility(control: IControlCard): IControlCard {
    // Check if isVisible is already set on componentProps or root
    const componentPropsVisibility = (control.componentProps as any)?.isVisible;
    const rootVisibility = (control as any).isVisible;

    // If isVisible is already defined on either, return control as-is
    if (componentPropsVisibility !== undefined || rootVisibility !== undefined) {
      return control;
    }

    // Set default isVisible to true on componentProps (source-of-truth)
    return {
      ...control,
      componentProps: {
        ...(control.componentProps ?? {}),
        isVisible: true,
      },
    };
  }

  private hasRequiredDescendant(controls: IControlCard[] | undefined): boolean {
    if (!controls || controls.length === 0) {
      return false;
    }

    return controls.some((control) => {
      // A subsection's own flag is derived from its children on every save, so a stale saved value must not count.
      const ownRequired = (control as any).controlType === 'subsection' ? false : this.isControlRequired(control);
      return ownRequired || this.hasRequiredDescendant(control.children);
    });
  }

  private isControlRequired(control: IControlCard | undefined): boolean {
    if (!control) {
      return false;
    }

    const componentRequired = (control.componentProps as any)?.required;
    if (typeof componentRequired === 'boolean') {
      return componentRequired;
    }

    const directRequired = (control as any).required;
    if (typeof directRequired === 'boolean') {
      return directRequired;
    }

    return Boolean(control.isRequired);
  }

  private buildAnswerOptions(control: IControlCard): FHIRAnswerOption[] {
    // Check both control.options and control.componentProps.options
    const options = (control.options ||
      (control.componentProps as { options?: ChoiceOptionLike[] } | undefined)?.options) as
      ChoiceOptionLike[] | undefined;

    if (options?.length) {
      return options.map((opt: ChoiceOptionLike) => {
        const label = String(opt.label ?? opt.display ?? opt.text ?? '');
        const code = String(opt.value ?? opt.code ?? label);
        const answerOption: FHIRAnswerOption = {
          valueCoding: {
            code,
            display: label,
          },
        };

        return answerOption;
      });
    }

    const count = control.optionCount || 0;
    const fallbackOptions: FHIRAnswerOption[] = [];
    for (let i = 0; i < count; i++) {
      fallbackOptions.push({ valueString: `Option ${i + 1}` });
    }
    return fallbackOptions;
  }

  private convertControlsWithErrors(
    controls: IControlCard[],
    parentPath: string,
    errors: ConversionError[],
    warnings: string[],
    bodyImageRegions?: any[]
  ): FHIRItem[] {
    const items: FHIRItem[] = [];

    for (let i = 0; i < controls.length; i++) {
      const control = controls[i];
      if (!control) continue;

      try {
        const item = this.convertControl(control, i, parentPath, bodyImageRegions);
        if (item) {
          items.push(item);
        } else {
          warnings.push(`Control "${control.question || control.id}" skipped - no metadata found`);
        }
      } catch (error) {
        errors.push({
          code: ERROR_CODES.CONTROL_CONVERSION_ERROR,
          message: error instanceof Error ? error.message : 'Failed to convert control',
          controlId: control.id,
          severity: 'error',
        });
      }
    }

    return items;
  }

  private countItems(controls: IControlCard[], metrics: ConversionMetrics): void {
    for (const control of controls) {
      // Handle tab groups specially - they're always layout controls
      if ((control as any).__tabGroup === true) {
        metrics.subsectionsCount++;
        metrics.totalItems++;
        if (control.children?.length) {
          this.countItems(control.children, metrics);
        }
        continue;
      }

      const controlType = (control as any).controlType || (control as any).type;
      const metadata = this.getCachedMetadata(controlType);
      if (metadata?.isLayoutControl) {
        metrics.subsectionsCount++;
      } else {
        metrics.controlsCount++;
      }
      metrics.totalItems++;

      if (control.children?.length) {
        this.countItems(control.children, metrics);
      }
    }
  }

  private createEmptyMetrics(): ConversionMetrics {
    return {
      totalItems: 0,
      sectionsCount: 1,
      subsectionsCount: 0,
      controlsCount: 0,
      extensionsCount: 0,
      durationMs: 0,
    };
  }

  private createResult<T>(
    success: boolean,
    data: T | undefined,
    errors: ConversionError[],
    warnings: string[],
    metrics: ConversionMetrics,
    startTime: number
  ): ConversionResult<T> {
    metrics.durationMs = performance.now() - startTime;
    return { success, data, errors, warnings, metrics };
  }

  /**
   * Validate controls for static dataMode Returns array of linkIds that have dataMode set to 'static' These controls
   * should use API mode instead when saving to FHIR
   */
  validateNoStaticDataMode(controls: IControlCard[]): string[] {
    const staticModeControls: string[] = [];

    const checkControl = (control: IControlCard) => {
      const dataMode = (control.componentProps as any)?.dataMode;
      const linkId = (control as any).linkId || control.id;

      // Check if this control has static dataMode ('default' mode bypasses this warning)
      if (dataMode === 'static') {
        staticModeControls.push(linkId);
      }

      // Recursively check children
      if (control.children && control.children.length > 0) {
        control.children.forEach(checkControl);
      }
    };

    controls.forEach(checkControl);
    return staticModeControls;
  }

  /**
   * Post-process FHIR items to add ext:hierarchy extension. Format: "Section > Subsection > ControlName" (always 3
   * levels).
   *
   * - Controls directly under section (no subsection): "Section > NA > ControlName"
   * - Controls under subsection: "Section > SubsectionName > ControlName"
   * - Nested controls (inside another control): hierarchy stops at level 1 parent control name
   */
  private addHierarchyExtensions(sectionItems: FHIRItem[]): void {
    const hierarchyExtUrl = `${EXT_PREFIX}:${EXT_KEYS.HIERARCHY}`;

    for (const section of sectionItems) {
      const sectionName = section.text || 'NA';
      if (!section.item) continue;

      for (const item of section.item) {
        if (this.isLayoutControl(item)) {
          // This is a subsection - process its children as level 1 controls
          const subsectionName = item.text || this.getControlIdentifier(item);
          if (item.item) {
            for (const child of item.item) {
              const controlName = child.text || this.getControlIdentifier(child);
              const hierarchy = `${sectionName} > ${subsectionName} > ${controlName}`;
              this.setHierarchyExtension(child, hierarchyExtUrl, hierarchy);

              // Nested controls inside this control get parent control as hierarchy endpoint
              if (child.item) {
                this.addNestedHierarchy(child.item, hierarchyExtUrl, sectionName, subsectionName, controlName);
              }
            }
          }
        } else {
          // Control directly under section (no subsection)
          const controlName = item.text || this.getControlIdentifier(item);
          const hierarchy = `${sectionName} > NA > ${controlName}`;
          this.setHierarchyExtension(item, hierarchyExtUrl, hierarchy);

          // Nested controls inside this control
          if (item.item) {
            this.addNestedHierarchy(item.item, hierarchyExtUrl, sectionName, 'NA', controlName);
          }
        }
      }
    }
  }

  /**
   * Add hierarchy extension to nested controls (depth > 1). Hierarchy stops at the level 1 parent control name - does
   * not append deeper. For tab-group items (children of a tabbed-layout), the tab's own label is used as the hierarchy
   * endpoint instead of the tabbed-layout control name.
   */
  private addNestedHierarchy(
    items: FHIRItem[],
    hierarchyExtUrl: string,
    sectionName: string,
    subsectionName: string,
    parentControlName: string
  ): void {
    const hierarchy = `${sectionName} > ${subsectionName} > ${parentControlName}`;
    for (const item of items) {
      if (this.isTabGroupItem(item)) {
        // Use the tab's own label as the hierarchy endpoint for this tab-group and its children
        const tabName = item.text || parentControlName;
        const tabHierarchy = `${sectionName} > ${subsectionName} > ${tabName}`;
        this.setHierarchyExtension(item, hierarchyExtUrl, tabHierarchy);
        if (item.item) {
          this.addNestedHierarchy(item.item, hierarchyExtUrl, sectionName, subsectionName, tabName);
        }
      } else {
        this.setHierarchyExtension(item, hierarchyExtUrl, hierarchy);
        // Recursively apply same hierarchy (does not go deeper)
        if (item.item) {
          this.addNestedHierarchy(item.item, hierarchyExtUrl, sectionName, subsectionName, parentControlName);
        }
      }
    }
  }

  /** Check if a FHIR item represents a tab-group (child of a tabbed-layout control) */
  private isTabGroupItem(item: FHIRItem): boolean {
    if (!item.extension) return false;
    return item.extension.some((ext) => {
      if (!ext.url?.includes('ui') || !ext.valueString) return false;
      try {
        const config = JSON.parse(ext.valueString);
        return config.__isTabContent === true || config.componentType === 'HxTabContent';
      } catch {
        return false;
      }
    });
  }

  /** Set the hierarchy extension on a FHIR item */
  private setHierarchyExtension(item: FHIRItem, url: string, value: string): void {
    if (!item.extension) {
      item.extension = [];
    }
    item.extension.push({ url, valueString: value });
  }

  /** Check if a FHIR item is a layout control (subsection) */
  private isLayoutControl(item: FHIRItem): boolean {
    if (item.type !== (FHIR_TYPES.GROUP as string)) return false;
    if (!item.extension) return false;

    return item.extension.some((ext) => {
      if (!ext.url?.includes('ui') || !ext.valueString) return false;
      try {
        const config = JSON.parse(ext.valueString);
        return config.category === 'layout' || config.isLayoutControl === true;
      } catch {
        return false;
      }
    });
  }

  /**
   * Extract a readable identifier for a FHIR item when text is empty. Tries: controlType from UI extension → control
   * type from linkId → linkId itself.
   */
  private getControlIdentifier(item: FHIRItem): string {
    // Try to extract controlType from UI config extension
    if (item.extension) {
      for (const ext of item.extension) {
        if (ext.url?.includes('ui') && ext.valueString) {
          try {
            const config = JSON.parse(ext.valueString);
            if (config.controlType) {
              return config.controlType;
            }
          } catch {
            // ignore parse errors
          }
        }
      }
    }

    // Fallback: extract control type from linkId (e.g., "section.textinput-abc123" → "textinput")
    if (item.linkId) {
      const lastSegment = item.linkId.split('.').pop() || item.linkId;
      const typePart = lastSegment.replace(/-[a-z0-9]+$/, '');
      if (typePart) return typePart;
    }

    return 'NA';
  }
}
